import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2, RotateCcw, PackageOpen } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

export default function AppointmentRefundModal({ bookingId, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [cancelToo, setCancelToo] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data: info, isLoading } = useQuery({
    queryKey: ["appt-refund-info", bookingId],
    queryFn: () => api.get(`/pos/appointments/${bookingId}/refund-info`).then((r) => r.data),
  });

  const isPackage = info?.method === "package";
  const maxR = info?.max_refundable ?? 0;

  const submit = async () => {
    setLoading(true);
    try {
      const body = isPackage ? { cancel: cancelToo } : { amount: amount === "" ? maxR : Number(amount), cancel: cancelToo };
      const { data } = await api.post(`/pos/appointments/${bookingId}/refund`, body);
      toast.success(isPackage
        ? `Credit restored — ${data.sessions_remaining} session(s) back on the package`
        : `Refunded ${money(Number(data.amount))}${data.method === "stripe" ? " via Stripe" : ""}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="appt-refund-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8">
        <button onClick={onClose} data-testid="appt-refund-close" className="absolute top-4 right-4 text-[#4A5D6B]"><X size={20} /></button>
        {isLoading || !info ? (
          <div className="py-10 text-center"><Loader2 className="animate-spin inline text-[#144F75]" /></div>
        ) : (
          <>
            <h3 className="font-serif text-2xl text-[#1A242B] mb-4">{isPackage ? "Restore credit" : "Refund appointment"}</h3>
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-4 space-y-1.5 text-sm mb-5" data-testid="appt-refund-summary">
              <div className="flex justify-between gap-3"><span className="text-[#4A5D6B]">Client</span><span className="font-medium text-[#1A242B]">{info.client_name || "—"}</span></div>
              <div className="flex justify-between gap-3"><span className="text-[#4A5D6B]">Service</span><span className="font-medium text-[#1A242B] text-right">{info.service_title}</span></div>
              <div className="flex justify-between gap-3"><span className="text-[#4A5D6B]">Date</span><span className="font-medium text-[#1A242B]">{fmtDate(info.date)} {fmtTimeRange(info.start_time)}</span></div>
              <div className="flex justify-between gap-3"><span className="text-[#4A5D6B]">{isPackage ? "Paid via" : "Amount paid"}</span>
                <span className="font-medium text-[#1A242B]" data-testid="appt-refund-paid">
                  {isPackage ? `Package credit${info.amount_paid > 0 ? ` (${money(info.amount_paid)} value)` : ""}` : `${money(Number(info.amount_paid))}`}
                </span></div>
              {info.already_refunded > 0 && <div className="flex justify-between gap-3"><span className="text-[#4A5D6B]">Already refunded</span><span className="font-medium text-[#B5794E]">{money(Number(info.already_refunded))}</span></div>}
            </div>

            {info.already_fully_refunded ? (
              <p className="text-sm text-[#B5794E]" data-testid="appt-refund-already">This appointment has already been refunded.</p>
            ) : isPackage ? (
              <p className="text-sm text-[#4A5D6B] mb-5">This session was paid with a package credit. Confirming will return
                <strong> one session credit</strong> to the client's package balance — no card refund is issued.</p>
            ) : info.method === "none" ? (
              <p className="text-sm text-[#B5794E]" data-testid="appt-refund-none">No recorded payment on this appointment to refund.</p>
            ) : (
              <div className="mb-5">
                <label className="text-xs text-[#4A5D6B]">Refund amount (up to ${maxR.toFixed(2)})</label>
                <input type="number" min="0.01" max={maxR} step="0.01" placeholder={maxR.toFixed(2)}
                  value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="appt-refund-amount" className="eyw-input w-full mt-1" />
                {info.method === "manual" && <p className="text-[11px] text-[#B5794E] mt-2">Paid by cash/offline — this records the refund; return the money to the client directly.</p>}
                {info.method === "stripe" && <p className="text-[11px] text-[#8FA89B] mt-2">Refunded to the original card via Stripe.</p>}
              </div>
            )}

            {!info.already_fully_refunded && info.method !== "none" && (
              <>
                {!["cancelled", "completed", "no_show", "refunded"].includes(info.status) && (
                  <label className="flex items-start gap-2 text-sm text-[#4A5D6B] mb-4 cursor-pointer">
                    <input type="checkbox" checked={cancelToo} onChange={(e) => setCancelToo(e.target.checked)} data-testid="appt-refund-cancel-too" className="mt-0.5 h-4 w-4 accent-[#144F75]" />
                    Also cancel this appointment and free the room/time slot
                  </label>
                )}
                <button onClick={submit} disabled={loading} data-testid="appt-refund-confirm" className="eyw-btn w-full justify-center disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={17} /> : isPackage ? <PackageOpen size={16} /> : <RotateCcw size={16} />}
                  {isPackage ? "Restore credit" : `Confirm refund${amount ? ` · ${money(Number(amount))}` : ""}`}{cancelToo ? " & cancel" : ""}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
